import { FormattedMessage, useIntl } from 'react-intl';
import { useEffect, useMemo, useState } from 'react';
import { useDataRequest } from 'hooks/useDataRequest';
import { Service, useProDataService } from 'services/data.service';
import { MeasureCardRow } from 'components/MeasureCardRow';
import { MetricsLoadingSpinner } from 'components/MetricsLoadingSpinner';
import { LineChartRenderer } from 'components/chart/renderer/LineChartRenderer';
import { useDatePickerLabels } from 'hooks/useDatePickerLabels';
import { TimeSeriesChartPageSection } from 'components/chart/TimeSeriesChartPageSection';
import { useInfoDrawer } from 'context/InfoDrawerContext';
import { Measure, useModelService } from 'services/model/model.service';
import { PageContainer, PageSectionTitle, Tile } from 'components/Layout/PageContainer';
import { createTimeSeriesChartConfig } from 'utils/createTimeSeriesChartConfig';
import { ReportLinkCard } from 'pages/insights-pro/common/reports/ReportLink';
import { PerformanceDashboardInfoContent } from './components/PerformanceDashboardInfoContent';
import { useDataServiceQueryResult } from 'hooks/useDataServiceQueryResult';

export function PerformanceDashboard() {
  const intl = useIntl();
  const labelContext = useDatePickerLabels();

  const { getMeasures, getAttribute } = useModelService();

  const [ordGmvVal, slsGmvVal, netSlsGmvVal, netSlsGmvValExt, ordNum, avgOrdGmvVal, retQtyRate, retGmvVal] =
    getMeasures([
      'ordGmvVal',
      'slsGmvValOD',
      'netSlsGmvValExpOD',
      'netSlsGmvValExtExpOD',
      'ordNum',
      'avgOrdGmvVal',
      'retQtyRateExpOD',
      'retGmvValExpOD',
    ]);

  const orderMeasures = useMemo(
    () => [ordGmvVal, slsGmvVal, netSlsGmvVal, netSlsGmvValExt],
    [ordGmvVal, slsGmvVal, netSlsGmvVal, netSlsGmvValExt]
  );
  const returnMeasures = useMemo(
    () => [ordNum, avgOrdGmvVal, retQtyRate, retGmvVal],
    [ordNum, avgOrdGmvVal, retQtyRate, retGmvVal]
  );

  const { setContent } = useInfoDrawer();
  useEffect(() => {
    const performanceDashboardInfo = {
      titleKey: 'Performance Dashboard',
      subtitleKey: 'info.content.all_dates_utc',
      content: <PerformanceDashboardInfoContent />,
    };

    setContent(performanceDashboardInfo);

    return () => {
      setContent(null);
    };
  }, [orderMeasures, returnMeasures, setContent]);

  const requestData = useDataRequest();

  const { fetchData } = useProDataService(Service.Performance);

  const dashboardState = useDataServiceQueryResult(
    requestData,
    data => fetchData('dashboard', data),
    data => data,
    ['insights-performance-dashboard', orderMeasures]
  );

  const [ordersTimeSeriesMeasure, setOrdersTimeSeriesMeasure] = useState(orderMeasures[0]);
  const [returnsTimeSeriesMeasure, setReturnsTimeSeriesMeasure] = useState(returnMeasures[0]);

  useEffect(() => {
    setOrdersTimeSeriesMeasure(orderMeasures[0]);
  }, [orderMeasures]);

  useEffect(() => {
    setReturnsTimeSeriesMeasure(returnMeasures[0]);
  }, [returnMeasures]);

  const ordersTimeSeriesState = useDataServiceQueryResult(
    requestData,
    data => fetchData(`dashboard/time-series/${ordersTimeSeriesMeasure.code}`, data),
    (response, request) =>
      new LineChartRenderer(
        createTimeSeriesChartConfig({
          getAttribute,
          measures: [ordersTimeSeriesMeasure],
          request,
          response,
          intl,
          labelContext,
          groupByAttribute: request.groupByAttribute,
        })
      ),
    ['insights-performance-orders-time-series', ordersTimeSeriesMeasure.code, intl.locale]
  );

  const returnsTimeSeriesState = useDataServiceQueryResult(
    requestData,
    data => fetchData(`dashboard/time-series/${returnsTimeSeriesMeasure.code}`, data),
    (response, request) =>
      new LineChartRenderer(
        createTimeSeriesChartConfig({
          getAttribute,
          measures: [returnsTimeSeriesMeasure],
          request,
          intl,
          response,
          labelContext,
          groupByAttribute: request.groupByAttribute,
        })
      ),
    ['insights-performance-returns-time-series', returnsTimeSeriesMeasure.code, intl.locale]
  );

  const timeSeriesOrderPerformanceMeasuresChangeHandler = (measure: Measure) => () => {
    if (measure.code !== ordersTimeSeriesMeasure.code) {
      setOrdersTimeSeriesMeasure(measure);
    }
  };

  const timeSeriesReturnPerformanceMeasureChangeHandler = (measure: Measure) => () => {
    if (measure.code !== returnsTimeSeriesMeasure.code) {
      setReturnsTimeSeriesMeasure(measure);
    }
  };

  if (dashboardState.isError) {
    return (
      <PageContainer>
        <Tile
          titleId="insights.performance"
          errorDetails={dashboardState.error}
          testId="insights-performance-dashboard-error"
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageSectionTitle>
        <FormattedMessage id="performance.track_revenue_order.title" />
      </PageSectionTitle>
      {ordersTimeSeriesState.isError ? (
        <Tile errorDetails={ordersTimeSeriesState.error} testId="insights-performance-orders-error" />
      ) : dashboardState.data ? (
        <>
          <MeasureCardRow
            measures={orderMeasures}
            testid="pro-performance-measure-card-set"
            totalsData={dashboardState.data}
            selectedMeasureCode={ordersTimeSeriesMeasure.code}
            onMeasureSelect={timeSeriesOrderPerformanceMeasuresChangeHandler}
          />
          <TimeSeriesChartPageSection
            timeSeriesMeasure={ordersTimeSeriesMeasure}
            timeSeriesRenderer={ordersTimeSeriesState.data}
            trackGroupByToggleEvents
          />
        </>
      ) : (
        <MetricsLoadingSpinner />
      )}
      <ReportLinkCard
        className={'xl:col-span-6'}
        titleId="insights.performance.reports.posting_date"
        descriptionId="insights.performance.reports.posting_date.description"
        linkUrl="reports/posting-date"
      />
      <ReportLinkCard
        className="xl:col-span-6"
        titleId="insights.performance.reports.demand_monitoring"
        descriptionId="insights.performance.reports.demand_monitoring.description"
        linkUrl="reports/demand-monitoring"
      />

      <PageSectionTitle>
        <FormattedMessage
          id="performance.monitor_efficiency.title"
          defaultMessage="Monitor revenue efficiency and customer returns"
        />
      </PageSectionTitle>

      {returnsTimeSeriesState.isError ? (
        <Tile errorDetails={returnsTimeSeriesState.error} testId="insights-performance-returns-error" />
      ) : dashboardState.data ? (
        <>
          <MeasureCardRow
            measures={returnMeasures}
            testid="measure-card-set-2"
            totalsData={dashboardState.data}
            selectedMeasureCode={returnsTimeSeriesMeasure.code}
            onMeasureSelect={timeSeriesReturnPerformanceMeasureChangeHandler}
          />
          <TimeSeriesChartPageSection
            timeSeriesMeasure={returnsTimeSeriesMeasure}
            timeSeriesRenderer={returnsTimeSeriesState.data}
            trackGroupByToggleEvents
          />
        </>
      ) : (
        <MetricsLoadingSpinner />
      )}
    </PageContainer>
  );
}
